// Course Overview — dashboard layout.
//
// One-screen scan of the course's state today. Above the fold:
//   * KPI strip (zones, area, health, water saved, active alerts, irrigation vendor)
//   * Priorities + briefing hero row
//   * Health / disease / spray / growth row
// Below that: alert inboxes, weather (non-demo), and the holes grid.
//
// Everything is packed into a 12-column CSS grid on desktop and collapses
// to a single column on mobile. No long flowing stack of full-width cards.

import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Map as MapIcon, ArrowRight } from 'lucide-react';
import type { Course, Hole, Zone, UserRole } from '@greeniq/shared';
import { api, ApiError } from '../api/client.js';
import { AlertsInbox } from '../components/AlertsInbox.js';
import { OnSiteStationCard } from '../components/OnSiteStationCard.js';
import { ForecastWidget } from '../components/ForecastWidget.js';
import { GrowthMetricsCard } from '../components/GrowthMetricsCard.js';
import { SprayWindowCard } from '../components/SprayWindowCard.js';
import { WeatherAlertsInbox } from '../components/WeatherAlertsInbox.js';
import { DiseaseRiskCard } from '../components/DiseaseRiskCard.js';
import { BriefingCard } from '../components/BriefingCard.js';
import { SensorAlertsInbox } from '../components/SensorAlertsInbox.js';
import { TodayPrioritiesCard } from '../components/TodayPrioritiesCard.js';
import { CourseHealthCard } from '../components/CourseHealthCard.js';
import { WaterSavedCard } from '../components/WaterSavedCard.js';
import { PageHeader } from '../components/ui/page-header';
import { StatTile } from '../components/ui/stat-tile';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';

interface CourseResponse {
  course: Course;
  holes: Hole[];
  zones: Zone[];
  role: UserRole;
}

interface BriefingTodayResponse {
  briefing: { id: number; forDate: string; generatedAt: string; headline: string } | null;
  items: Array<unknown>;
}

export function CourseHomePage() {
  const { courseSlug } = useParams();
  const [data, setData] = useState<CourseResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [briefingGeneratedAt, setBriefingGeneratedAt] = useState<string | null>(null);

  useEffect(() => {
    if (!courseSlug) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await api.get<CourseResponse>(`/courses/${courseSlug}`);
        if (!cancelled) setData(resp);
      } catch (err) {
        if (!cancelled) setError(err instanceof ApiError ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [courseSlug]);

  // Fetch just the briefing metadata (timestamp) for the freshness chip in
  // the page header. The BriefingCard component fetches the full payload
  // separately; this is a lightweight probe.
  useEffect(() => {
    if (!courseSlug) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await api.get<BriefingTodayResponse>(`/courses/${courseSlug}/briefings/today`);
        if (!cancelled) setBriefingGeneratedAt(resp.briefing?.generatedAt ?? null);
      } catch {
        /* silent — chip just hides */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [courseSlug]);

  if (error) {
    return (
      <div className="mx-auto max-w-7xl p-8">
        <div className="rounded-lg border border-[color:var(--color-error)]/30 bg-error-muted p-4 text-sm text-fg">
          {error}
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="mx-auto max-w-7xl p-8 text-sm text-fg-muted">Loading…</div>;
  }

  const { course, holes, zones } = data;
  const totalAcres = (zones.reduce((s, z) => s + Number(z.areaSqft), 0) / 43560).toFixed(1);

  // Group zones by hole for the demo footer grid.
  const holesById = new Map(holes.map((h) => [h.id, h]));
  const zonesByHole = new Map<number | 'other', Zone[]>();
  for (const z of zones) {
    const key = z.holeId ?? 'other';
    if (!zonesByHole.has(key)) zonesByHole.set(key, []);
    zonesByHole.get(key)!.push(z);
  }
  const demoHoleGroups = Array.from(zonesByHole.entries())
    .map(([holeKey, zs]) => ({
      holeKey,
      hole: typeof holeKey === 'number' ? holesById.get(holeKey) ?? null : null,
      zones: zs,
    }))
    .sort((a, b) => {
      const an = a.hole?.holeNumber ?? Number.MAX_SAFE_INTEGER;
      const bn = b.hole?.holeNumber ?? Number.MAX_SAFE_INTEGER;
      return an - bn;
    });

  const freshness = briefingFreshness(briefingGeneratedAt);

  return (
    <div className="mx-auto max-w-7xl p-6 md:p-10">
      <PageHeader
        eyebrow={course.demoMode ? 'Overview · demo view' : 'Overview'}
        title={course.name}
        description={`${[course.city, course.state].filter(Boolean).join(', ') || '—'} · USDA ${course.usdaZone ?? 'unknown'}`}
        actions={
          <div className="flex items-center gap-2">
            {freshness && (
              <Badge
                variant={freshness.isStale ? 'warning' : 'default'}
                title={`Briefing generated ${freshness.absoluteLabel}`}
              >
                Briefing · {freshness.relativeLabel}
              </Badge>
            )}
            <Button asChild size="sm">
              <Link to={`/app/${course.slug}/map`}>
                <MapIcon size={14} /> Open course map
              </Link>
            </Button>
          </div>
        }
      />

      {/* KPI strip — compact one-row scan */}
      <div className="mt-6 grid grid-cols-2 gap-2.5 md:grid-cols-3 lg:grid-cols-6">
        <StatTile
          label={course.demoMode ? 'Zones in view' : 'Zones'}
          value={String(zones.length)}
        />
        <StatTile
          label={course.demoMode ? 'Area in view' : 'Area'}
          value={`${totalAcres} ac`}
        />
        <StatTile label="Holes" value={String(holes.length || demoHoleGroups.length)} />
        <StatTile
          label="Irrigation"
          value={course.primaryIrrigationVendor?.replace(/_/g, ' ') ?? '—'}
        />
        <StatTile label="USDA zone" value={course.usdaZone ?? '—'} />
        <StatTile
          label="Construction"
          value={course.constructionType?.replace(/_/g, ' ') ?? '—'}
        />
      </div>

      {courseSlug && (
        <>
          {/* HERO ROW — priorities (3/5) + briefing (2/5). 5-col grid at lg+
              gives the briefing enough width to fit headlines without each
              word wrapping to its own line. Each card scrolls inside. */}
          <div className="mt-6 grid gap-4 lg:grid-cols-5">
            <div className="lg:col-span-3 [&>section]:max-h-[28rem] [&>section]:overflow-y-auto">
              <TodayPrioritiesCard courseSlug={courseSlug} />
            </div>
            <div className="lg:col-span-2 [&>section]:max-h-[28rem] [&>section]:overflow-y-auto">
              <BriefingCard courseSlug={courseSlug} />
            </div>
          </div>

          {/* METRIC ROW — health + water + disease + spray on one row at lg+
              (sidebar eats 240px, so xl breakpoint never fires inside main).
              `[&_section]:!mt-0` neutralizes the mt-6 some cards bake in,
              so all four sections align to the top of their grid cell. */}
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4 [&_section]:!mt-0 [&>*>section]:h-full [&>*>section]:max-h-[18rem] [&>*>section]:overflow-y-auto">
            <div><CourseHealthCard courseSlug={courseSlug} /></div>
            <div><WaterSavedCard courseSlug={courseSlug} /></div>
            <div><DiseaseRiskCard courseSlug={courseSlug} /></div>
            <div><SprayWindowCard courseSlug={courseSlug} /></div>
          </div>

          {/* CONTEXT ROW — growth + weather (non-demo) */}
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 [&_section]:!mt-0 [&>*>section]:h-full [&>*>section]:max-h-[18rem] [&>*>section]:overflow-y-auto">
            <div><GrowthMetricsCard courseSlug={courseSlug} /></div>
            {!course.demoMode && (
              <>
                <div><OnSiteStationCard courseSlug={courseSlug} /></div>
                <div><ForecastWidget courseSlug={courseSlug} /></div>
              </>
            )}
          </div>

          {/* ALERTS ROW — Sensor + Satellite (the AGEYE-specific intelligence)
              span the full row 50/50. Weather alerts live on the Weather page
              and were dropped here because they often render empty (the
              component returns null when there are zero unacknowledged), which
              left a visible gap in the middle of the row. */}
          <div className="mt-4 grid gap-4 lg:grid-cols-2 [&_section]:!mt-0 [&>*>section]:h-full [&>*>section]:max-h-[18rem] [&>*>section]:overflow-y-auto">
            <div><SensorAlertsInbox courseSlug={courseSlug} /></div>
            <div><AlertsInbox courseSlug={courseSlug} /></div>
          </div>

          {/* WEATHER ALERTS — only renders when there's something to show
              (component returns null on empty). When present it spans full
              width below the sensor + satellite row. */}
          <div className="mt-4 [&_section]:!mt-0 [&>section]:max-h-[18rem] [&>section]:overflow-y-auto">
            <WeatherAlertsInbox courseSlug={courseSlug} />
          </div>
        </>
      )}

      {/* HOLES / ZONES footer */}
      <h2 className="mt-10 font-display text-lg font-semibold text-fg">
        {course.demoMode ? 'Holes' : 'Zones'}
      </h2>

      {course.demoMode ? (
        <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {demoHoleGroups.map((group) => {
            const headline =
              group.hole != null
                ? `Hole ${group.hole.holeNumber}${group.hole.par ? ` · Par ${group.hole.par}` : ''}`
                : 'Other features';
            const holeAcres = (
              group.zones.reduce((s, z) => s + Number(z.areaSqft), 0) / 43560
            ).toFixed(2);
            const surfaceCounts = group.zones.reduce<Record<string, number>>((acc, z) => {
              acc[z.zoneType] = (acc[z.zoneType] ?? 0) + 1;
              return acc;
            }, {});
            const surfaceSummary = Object.entries(surfaceCounts)
              .map(([t, n]) => `${n} ${t.replace(/_/g, ' ')}`)
              .join(' · ');
            const target =
              group.hole != null ? `/app/${course.slug}/holes/${group.hole.holeNumber}` : null;
            const cardInner = (
              <>
                <div className="flex items-baseline justify-between gap-3">
                  <div className="font-display text-base font-semibold text-fg">{headline}</div>
                  <div className="text-[11px] text-fg-muted">{holeAcres} ac</div>
                </div>
                <div className="mt-1 text-[11px] text-fg-muted">
                  {surfaceSummary || `${group.zones.length} features`}
                </div>
                {target && (
                  <div className="mt-2 flex items-center justify-end text-[11px] text-greeniq-400">
                    Open hole <ArrowRight size={11} className="ml-1 inline" />
                  </div>
                )}
              </>
            );
            return target ? (
              <Link
                key={String(group.holeKey)}
                to={target}
                className="group block rounded-md border border-border bg-bg-card p-3 transition-all duration-[150ms] hover:-translate-y-0.5 hover:border-greeniq-500/30 hover:shadow-md"
              >
                {cardInner}
              </Link>
            ) : (
              <div
                key={String(group.holeKey)}
                className="rounded-md border border-border bg-bg-card p-3"
              >
                {cardInner}
              </div>
            );
          })}
          {demoHoleGroups.length === 0 && (
            <div className="col-span-full rounded-lg border border-dashed border-border bg-bg-card p-8 text-center text-sm text-fg-muted">
              No zones yet. Trace the course in the Course Map to get started.
            </div>
          )}
        </div>
      ) : (
        <div className="mt-3 grid gap-3 md:grid-cols-3 xl:grid-cols-4">
          {zones.map((z) => (
            <Link
              key={z.id}
              to={`/app/${course.slug}/zones/${z.id}`}
              className={cn(
                'group block rounded-md border border-border bg-bg-card p-3',
                'transition-all duration-[150ms] hover:-translate-y-0.5 hover:border-greeniq-500/30 hover:shadow-md',
              )}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-fg">{z.name}</div>
                  <div className="mt-0.5 text-[11px] text-fg-muted">
                    {z.zoneType.replace(/_/g, ' ')} · {Math.round(Number(z.areaSqft)).toLocaleString()} sqft
                  </div>
                </div>
                <Badge variant="default" className="shrink-0">
                  {z.grassSpecies ?? 'turf'}
                </Badge>
              </div>
              <div className="mt-2 flex items-center justify-end text-[11px] text-fg-muted transition-colors group-hover:text-greeniq-400">
                Open <ArrowRight size={11} className="ml-1" />
              </div>
            </Link>
          ))}
          {zones.length === 0 && (
            <div className="col-span-full rounded-lg border border-dashed border-border bg-bg-card p-8 text-center text-sm text-fg-muted">
              No zones yet. Trace the course in the Course Map to get started.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function briefingFreshness(iso: string | null): {
  relativeLabel: string;
  absoluteLabel: string;
  isStale: boolean;
} | null {
  if (!iso) return null;
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return null;
  const ageMs = Date.now() - t.getTime();
  const ageHours = ageMs / 3_600_000;
  const isStale = ageHours >= 4;
  let relativeLabel: string;
  if (ageHours < 1) {
    const m = Math.max(1, Math.round(ageMs / 60_000));
    relativeLabel = `${m}m ago`;
  } else if (ageHours < 24) {
    relativeLabel = `${Math.round(ageHours)}h ago`;
  } else {
    relativeLabel = `${Math.round(ageHours / 24)}d ago`;
  }
  return {
    relativeLabel,
    absoluteLabel: t.toLocaleString(),
    isStale,
  };
}
